import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Loader2, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", phone: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Request received. Our team will be in touch within one business day.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phoneHref}` },
    { icon: MapPin, label: "Office", value: COMPANY.address.join(", ") },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-silver py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="eb-overline text-brand-blue">Get In Touch</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 text-3xl sm:text-4xl lg:text-[2.7rem] leading-[1.08] tracking-tight font-black text-navy-900">
                Request a consultation.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-navy-700/75 font-medium">
                Tell us about your fleet and objectives. A senior advisor will
                respond personally to arrange an initial, no-obligation discussion.
              </p>
            </Reveal>

            <div className="mt-10 space-y-5">
              {contactItems.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center bg-navy-900 text-white">
                      <Icon size={18} />
                    </span>
                    <div>
                      <div className="eb-overline text-silver-400">{c.label}</div>
                      <div className="mt-1 font-semibold text-navy-900">{c.value}</div>
                    </div>
                  </div>
                );
                return (
                  <Reveal key={c.label} delay={0.12}>
                    {c.href ? (
                      <a data-testid={`contact-${c.label.toLowerCase()}`} href={c.href} className="block group">
                        <div className="transition-opacity group-hover:opacity-70">{inner}</div>
                      </a>
                    ) : (
                      <div data-testid={`contact-${c.label.toLowerCase()}`}>{inner}</div>
                    )}
                  </Reveal>
                );
              })}
              <Reveal delay={0.15}>
                <div className="flex items-center gap-3 pt-2 text-sm text-navy-700/60 font-medium">
                  <Clock size={16} className="text-brand-blue" />
                  Response within one business day · Sun to Thu, 09:00 to 18:00 GST
                </div>
              </Reveal>
            </div>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-white border border-silver-200 p-7 sm:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-navy-900 font-semibold">Full name *</Label>
                  <Input id="name" data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Jane Mariner" className="h-12 rounded-sm border-silver-200 focus-visible:ring-brand-blue" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-navy-900 font-semibold">Email *</Label>
                  <Input id="email" data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} placeholder="jane@company.com" className="h-12 rounded-sm border-silver-200 focus-visible:ring-brand-blue" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-navy-900 font-semibold">Company</Label>
                  <Input id="company" data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Shipping Co. Ltd" className="h-12 rounded-sm border-silver-200 focus-visible:ring-brand-blue" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-navy-900 font-semibold">Phone</Label>
                  <Input id="phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="+971 50 000 0000" className="h-12 rounded-sm border-silver-200 focus-visible:ring-brand-blue" />
                </div>
              </div>

              <div className="mt-5 space-y-2">
                <Label className="text-navy-900 font-semibold">Service of interest</Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger data-testid="contact-select-service" className="h-12 rounded-sm border-silver-200 focus:ring-brand-blue">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5 space-y-2">
                <Label htmlFor="message" className="text-navy-900 font-semibold">How can we help? *</Label>
                <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={update("message")} rows={5} placeholder="Tell us about your fleet, challenge or objective…" className="rounded-sm border-silver-200 focus-visible:ring-brand-blue resize-none" />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="mt-7 h-13 w-full rounded-sm bg-brand-blue py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-navy-900 disabled:opacity-70"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2"><Loader2 size={17} className="animate-spin" /> Sending…</span>
                ) : (
                  <span className="inline-flex items-center gap-2">Submit Consultation Request <ArrowRight size={17} /></span>
                )}
              </Button>
              <p className="mt-4 text-xs text-navy-700/50 text-center">
                By submitting, you agree to be contacted by Echo Bravo regarding your enquiry.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
